import React from 'react';
import { motion } from 'framer-motion';
import Hero from '../components/ui/Hero';
import Section from '../components/ui/Section';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiHeart, FiChurch, FiArrowRight, FiMail, FiPhone } = FiIcons;

const GetInvolved = () => {
  const involvementOptions = [
    {
      icon: FiUsers,
      title: 'Volunteer Team Member',
      description: 'Join our regular outreach teams on college campuses and in cities across Florida.',
      commitment: 'Weekly commitment of 4-6 hours',
      requirements: [
        'Heart for evangelism and discipleship',
        'Available for weekly outreach',
        'Complete volunteer training program',
        'Commitment to team prayer and planning'
      ],
      action: 'Apply to Volunteer',
      href: '#volunteer-form'
    },
    {
      icon: FiHeart,
      title: 'Prayer Partner',
      description: 'Support the ministry through consistent prayer for our teams, students, and outreach efforts.',
      commitment: 'Ongoing prayer support',
      requirements: [
        'Commitment to pray regularly for ministry',
        'Receive and respond to prayer requests',
        'Participate in monthly prayer calls',
        'Heart for the lost and spiritual breakthrough'
      ],
      action: 'Join Prayer Team',
      href: '#prayer-form'
    },
    {
      icon: FiChurch,
      title: 'Church Partnership',
      description: 'Partner with us as a local church to support outreach efforts and disciple new believers.',
      commitment: 'Ongoing partnership relationship',
      requirements: [
        'Aligned with our mission and values',
        'Willing to support financially or with volunteers',
        'Heart for college-age ministry',
        'Commitment to disciple new converts'
      ],
      action: 'Explore Partnership',
      href: '#church-form'
    }
  ];

  const volunteerRoles = [
    {
      title: 'Campus Evangelists',
      description: 'Engage students in conversations and share the gospel on college campuses.',
      time: 'Weekday lunch hours'
    },
    {
      title: 'Bible Study Leaders',
      description: 'Lead small group Bible studies for new believers and seekers.',
      time: 'Weekly evening groups'
    },
    {
      title: 'City Outreach Team',
      description: 'Participate in street evangelism and urban ministry initiatives.',
      time: 'Weekend outreach events'
    },
    {
      title: 'Prayer Walk Coordinators',
      description: 'Lead prayer walks through campuses and neighborhoods.',
      time: 'Flexible scheduling'
    },
    {
      title: 'Event Support',
      description: 'Help with setup, logistics, and coordination for special events.',
      time: 'Event-based commitment'
    },
    {
      title: 'Digital Ministry',
      description: 'Support online outreach through social media and digital engagement.',
      time: 'Flexible, remote work'
    }
  ];

  return (
    <div>
      <Hero
        title="Get Involved"
        description="God is calling ordinary people to be part of extraordinary transformation. Whether you're a student, professional, retiree, or church leader, there's a place for you in Love 180 ministry."
        primaryCta={{
          text: 'Start Your Application',
          href: '#involvement-options'
        }}
        backgroundImage="https://images.unsplash.com/photo-1529390079861-591de354faf5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1950&q=80"
      />

      {/* Involvement Options */}
      <Section id="involvement-options">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
            Ways to Get Involved
          </h2>
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
            Choose the involvement level that fits your calling, schedule, and season of life. Every role is vital to seeing lives transformed.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {involvementOptions.map((option, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="p-6 h-full flex flex-col">
                <div className="flex-1">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <SafeIcon icon={option.icon} className="w-6 h-6 text-primary-600" />
                  </div>
                  
                  <h3 className="text-xl font-semibold text-center mb-3">{option.title}</h3>
                  <p className="text-neutral-600 text-center mb-4">{option.description}</p>
                  
                  <div className="bg-primary-50 p-4 rounded-lg mb-4">
                    <h4 className="font-semibold text-primary-900 mb-2">Commitment Level</h4>
                    <p className="text-primary-700 text-sm">{option.commitment}</p>
                  </div>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold mb-3">Requirements:</h4>
                    <ul className="space-y-2">
                      {option.requirements.map((req, reqIndex) => (
                        <li key={reqIndex} className="flex items-start space-x-2 text-sm text-neutral-600">
                          <div className="w-1.5 h-1.5 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                          <span>{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                <Button variant="primary" className="w-full" href={option.href}>
                  {option.action}
                  <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-2" />
                </Button>
              </Card>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Volunteer Roles */}
      <Section background="gray">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-neutral-900 mb-4">
            Volunteer Opportunities
          </h2>
          <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
            We have a variety of volunteer roles to match different gifts, interests, and availability. Find where you fit best in advancing God's kingdom.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {volunteerRoles.map((role, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="p-5 h-full">
                <h3 className="font-semibold mb-2">{role.title}</h3>
                <p className="text-neutral-600 text-sm mb-3">{role.description}</p>
                <div className="text-xs text-primary-600 bg-primary-50 px-2 py-1 rounded-full inline-block">
                  {role.time}
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Volunteer Application Form */}
      <Section id="volunteer-form">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Volunteer Application
            </h2>
            <p className="text-lg text-neutral-600">
              Take the first step in joining our team. We'll be in touch within 48 hours to discuss next steps.
            </p>
          </div>

          <Card className="p-8">
            <form className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  required
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  required
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Age Range
                </label>
                <select className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent">
                  <option value="">Select age range</option>
                  <option value="18-24">18-24</option>
                  <option value="25-34">25-34</option>
                  <option value="35-44">35-44</option>
                  <option value="45-54">45-54</option>
                  <option value="55+">55+</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Church/Home Church
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Name of your home church"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Areas of Interest (Select all that apply)
                </label>
                <div className="space-y-2">
                  {volunteerRoles.slice(0, 4).map((role, index) => (
                    <label key={index} className="flex items-center space-x-2">
                      <input type="checkbox" className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500" />
                      <span className="text-sm">{role.title}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Availability
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {['Weekday Mornings', 'Weekday Afternoons', 'Weekday Evenings', 'Saturday Mornings', 'Saturday Afternoons', 'Sunday Afternoons'].map((time, index) => (
                    <label key={index} className="flex items-center space-x-2">
                      <input type="checkbox" className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500" />
                      <span className="text-sm">{time}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Tell us about your faith journey *
                </label>
                <textarea
                  rows={4}
                  required
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Share briefly about your relationship with Jesus and why you want to be involved in evangelism..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Previous ministry experience
                </label>
                <textarea
                  rows={3}
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Describe any previous ministry or volunteer experience..."
                />
              </div>

              <Button type="submit" variant="primary" size="lg" className="w-full">
                Submit Application
              </Button>
            </form>
          </Card>
        </div>
      </Section>

      {/* Prayer Partner Section */}
      <Section id="prayer-form" background="gray">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-neutral-900 mb-4">
            Join Our Prayer Team
          </h2>
          <p className="text-lg text-neutral-600 mb-8">
            Prayer is the foundation of everything we do. Join hundreds of prayer partners who intercede for our ministry, students, and outreach efforts.
          </p>

          <Card className="p-8">
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="First Name"
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <input
                  type="text"
                  placeholder="Last Name"
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <input
                type="email"
                placeholder="Email Address"
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <div>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500" />
                  <span className="text-sm">I want to receive weekly prayer requests via email</span>
                </label>
              </div>
              <div>
                <label className="flex items-center space-x-2">
                  <input type="checkbox" className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500" />
                  <span className="text-sm">I'd like to join monthly prayer calls</span>
                </label>
              </div>
              <Button variant="primary" size="lg" className="w-full">
                <SafeIcon icon={FiHeart} className="w-4 h-4 mr-2" />
                Join Prayer Team
              </Button>
            </div>
          </Card>
        </div>
      </Section>

      {/* Church Partnership Section */}
      <Section id="church-form">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Church Partnership Opportunities
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              Partner with Love 180 to multiply your church's impact on college campuses and in urban centers. Together, we can reach more lives for Christ.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <Card className="p-6">
              <h3 className="text-xl font-semibold mb-4">Partnership Benefits</h3>
              <ul className="space-y-2">
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-sm text-neutral-600">Direct pipeline for new believers to connect with your church</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-sm text-neutral-600">Opportunities for your members to engage in evangelism</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-sm text-neutral-600">Training and equipping for your evangelism teams</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-sm text-neutral-600">Regular reports on ministry impact and fruit</span>
                </li>
              </ul>
            </Card>

            <Card className="p-6">
              <h3 className="text-xl font-semibold mb-4">Partnership Levels</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium">Supporting Partner</h4>
                  <p className="text-sm text-neutral-600">Financial support and prayer partnership</p>
                </div>
                <div>
                  <h4 className="font-medium">Active Partner</h4>
                  <p className="text-sm text-neutral-600">Volunteer teams + financial support</p>
                </div>
                <div>
                  <h4 className="font-medium">Strategic Partner</h4>
                  <p className="text-sm text-neutral-600">Full collaboration in ministry planning and execution</p>
                </div>
              </div>
            </Card>
          </div>

          <Card className="p-8">
            <h3 className="text-xl font-semibold mb-6">Church Partnership Interest Form</h3>
            <form className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Church Name *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    City/Location *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Pastor Name *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Contact Email *
                  </label>
                  <input
                    type="email"
                    required
                    className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Church Size (Approximate attendance)
                </label>
                <select className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent">
                  <option value="">Select size</option>
                  <option value="50-100">50-100</option>
                  <option value="100-200">100-200</option>
                  <option value="200-500">200-500</option>
                  <option value="500-1000">500-1,000</option>
                  <option value="1000+">1,000+</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Interest Level
                </label>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2">
                    <input type="radio" name="interest" value="supporting" className="text-primary-600 focus:ring-primary-500" />
                    <span className="text-sm">Supporting Partner (Prayer & Financial)</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="radio" name="interest" value="active" className="text-primary-600 focus:ring-primary-500" />
                    <span className="text-sm">Active Partner (Volunteers + Support)</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="radio" name="interest" value="strategic" className="text-primary-600 focus:ring-primary-500" />
                    <span className="text-sm">Strategic Partner (Full Collaboration)</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Tell us about your church's heart for evangelism
                </label>
                <textarea
                  rows={4}
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Share about your church's current evangelism efforts and vision for reaching the lost..."
                />
              </div>

              <Button type="submit" variant="primary" size="lg" className="w-full">
                <SafeIcon icon={FiChurch} className="w-4 h-4 mr-2" />
                Submit Partnership Inquiry
              </Button>
            </form>
          </Card>
        </div>
      </Section>

      {/* Contact Information */}
      <Section background="gray">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-neutral-900 mb-4">
            Questions About Getting Involved?
          </h2>
          <p className="text-lg text-neutral-600 mb-8">
            We'd love to talk with you about how you can be part of what God is doing through Love 180.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="primary" size="lg">
              <SafeIcon icon={FiMail} className="w-4 h-4 mr-2" />
              Email Us
            </Button>
            <Button variant="outline" size="lg">
              <SafeIcon icon={FiPhone} className="w-4 h-4 mr-2" />
              Call (555) 123-4567
            </Button>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default GetInvolved;